import { Box, HStack, Spacer, Text } from "@chakra-ui/react";
import * as React from "react";

import {
  ConversionAction,
  ConversionActionPlatform,
  ConversionActionSyncedEvent,
  getConversionActionSyncedEvents,
} from "../api/conversion-action";
import { useLoading, useToaster } from "../hooks";
import { brand } from "../lib/brand";
import { Button } from "./button";
import DataTable, { DataTableColumn } from "./data-table";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "./dialog";
import { StatusPill } from "./status-pill";

interface ConversionActionSyncedEventsDialogProps {
  action: ConversionAction | null;
  isOpen: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  workspaceSlug: string;
}

const PLATFORM_LABELS: Record<ConversionActionPlatform, string> = {
  [ConversionActionPlatform.GOOGLE_ADS]: "Google Ads",
  [ConversionActionPlatform.META]: "Meta",
};

export function ConversionActionSyncedEventsDialog({
  action,
  isOpen,
  onOpenChange,
  workspaceSlug,
}: ConversionActionSyncedEventsDialogProps) {
  const [events, setEvents] = React.useState<ConversionActionSyncedEvent[]>([]);
  const [total, setTotal] = React.useState(0);
  const [hasLoaded, setHasLoaded] = React.useState(false);
  const [isLoading, withLoading] = useLoading();
  const withToaster = useToaster();

  React.useEffect(() => {
    if (!isOpen || !action) return;
    withLoading(async () => {
      setEvents([]);
      setTotal(0);
      setHasLoaded(false);
      await withToaster(async () => {
        const data = await getConversionActionSyncedEvents(
          workspaceSlug,
          action.id,
        );
        setEvents(data.items);
        setTotal(data.meta.total);
        setHasLoaded(true);
      });
    });
  }, [action, isOpen, withLoading, withToaster, workspaceSlug]);

  const handleExport = () => {
    if (!action) return;
    const rows = [
      ["date", "click-id", "value", "status"],
      ...events.map((e) => [
        e["created-at"],
        e.gclid ?? e.fbclid ?? "",
        String(e.value),
        e["error-code"] ?? "sent",
      ]),
    ];
    const csv = rows
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${action.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-conversions.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const columns: DataTableColumn<ConversionActionSyncedEvent>[] = [
    {
      name: "Date",
      cell: ({ value }) => (
        <Text color="fg.muted" whiteSpace="nowrap">
          {new Date(value["created-at"]).toLocaleString()}
        </Text>
      ),
    },
    {
      name: "Value",
      cell: ({ value }) => <Text>{value.value}</Text>,
    },
    {
      name: "Status",
      cell: ({ value }) =>
        value["error-code"] ? (
          <StatusPill tone="red">Error</StatusPill>
        ) : (
          <StatusPill tone="green">Sent</StatusPill>
        ),
    },
  ];

  return (
    <DialogRoot open={isOpen} onOpenChange={onOpenChange} size="xl">
      <DialogContent maxWidth={"680px" as any}>
        <DialogCloseTrigger />
        <DialogHeader display="block">
          <DialogTitle>{action?.name ?? ""}</DialogTitle>
          <Text color="fg.muted" fontFamily="inter" fontSize="sm" marginTop="1">
            {action ? PLATFORM_LABELS[action.platform] : ""} · Conversions sent
            by {brand.productName}
          </Text>
        </DialogHeader>
        <DialogBody>
          {hasLoaded && events.length === 0 ? (
            <Box paddingY="8" textAlign="center">
              <Text color="fg.muted" fontSize="sm">
                No conversions sent yet.
              </Text>
            </Box>
          ) : (
            <Box maxHeight={"400px" as any} overflowY="auto">
              <DataTable
                columns={columns}
                data={events}
                skeleton={isLoading && !hasLoaded ? 3 : undefined}
                template="minmax(220px,1fr) 120px 140px"
              />
            </Box>
          )}
        </DialogBody>
        <DialogFooter>
          <HStack width="full">
            <Text color="fg.muted" fontSize="sm">
              Showing: {events.length} of {total}
            </Text>
            <Spacer />
            <Button
              variant="surface"
              onClick={handleExport}
              disabled={events.length === 0}
            >
              Export CSV
            </Button>
            <Button
              variant="solid"
              onClick={() => onOpenChange({ open: false })}
            >
              Done
            </Button>
          </HStack>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
